import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { MainLayout } from './layouts/MainLayout';
import { ProtectedRoute } from './layouts/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProductsListPage } from './pages/products/ProductsListPage';
import { ProductFormPage } from './pages/products/ProductFormPage';
import { BrandsListPage } from './pages/brands/BrandsListPage';
import { BrandFormPage } from './pages/brands/BrandFormPage';
import { CategoriesListPage } from './pages/categories/CategoriesListPage';
import { CategoryFormPage } from './pages/categories/CategoryFormPage';
import { PromoImagesPage } from './pages/promo-images/PromoImagesPage';
import { FlyersListPage } from './pages/flyers/FlyersListPage';
import { FlyerEditorPage } from './pages/flyers/FlyerEditorPage';
import { ActiveFlyersPage } from './pages/flyers/ActiveFlyersPage';
import { FlyerPreviewPage } from './pages/flyers/FlyerPreviewPage';
import { ApprovalsPage } from './pages/approvals/ApprovalsPage';
import { ApprovalReviewPage } from './pages/approvals/ApprovalReviewPage';
import { UsersListPage } from './pages/admin/UsersListPage';
import { UserFormPage } from './pages/admin/UserFormPage';
import { IconsListPage } from './pages/icons/IconsListPage';
import { IconFormPage } from './pages/icons/IconFormPage';
import { AnnouncementsListPage } from './pages/announcements/AnnouncementsListPage';
import { AnnouncementFormPage } from './pages/announcements/AnnouncementFormPage';
import { NewsPage } from './pages/announcements/NewsPage';
import { ActiveFlyersProductsPage } from './pages/products/ActiveFlyersProductsPage';

function App() {
  const { loadFromStorage, isAuthenticated } = useAuthStore();

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Admin Routes */}
          <Route
            path="admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UsersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/users/new"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/users/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/icons"
            element={
              <ProtectedRoute allowedRoles={['admin', 'pre_approver', 'approver']}>
                <IconsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/icons/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'pre_approver', 'approver']}>
                <IconFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/icons/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'pre_approver', 'approver']}>
                <IconFormPage />
              </ProtectedRoute>
            }
          />

          {/* Brands Routes (Admin only) */}
          <Route
            path="brands"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver', 'pre_approver']}>
                <BrandsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="brands/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver']}>
                <BrandFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="brands/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver', 'pre_approver']}>
                <BrandFormPage />
              </ProtectedRoute>
            }
          />

          {/* Categories Routes (Admin only) */}
          <Route
            path="categories"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver']}>
                <CategoriesListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="categories/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver']}>
                <CategoryFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="categories/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver']}>
                <CategoryFormPage />
              </ProtectedRoute>
            }
          />

          {/* Announcements Routes (Approver + Admin - management) */}
          {/* Čtecí přehled novinek – pro všechny přihlášené role */}
          <Route
            path="news"
            element={
              <ProtectedRoute>
                <NewsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="announcements"
            element={
              <ProtectedRoute allowedRoles={['approver', 'admin']}>
                <AnnouncementsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="announcements/new"
            element={
              <ProtectedRoute allowedRoles={['approver', 'admin']}>
                <AnnouncementFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="announcements/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['approver', 'admin']}>
                <AnnouncementFormPage />
              </ProtectedRoute>
            }
          />

          {/* Products in active flyers (Admin + Approver) */}
          <Route
            path="admin/active-flyers-products"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver']}>
                <ActiveFlyersProductsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="products"
            element={
              <ProtectedRoute allowedRoles={['supplier']}>
                <ProductsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="products/new"
            element={
              <ProtectedRoute allowedRoles={['supplier']}>
                <ProductFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="products/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['supplier']}>
                <ProductFormPage />
              </ProtectedRoute>
            }
          />

          {/* Promo Images Routes (Supplier + Admin) */}
          <Route
            path="promo-images"
            element={
              <ProtectedRoute allowedRoles={['supplier', 'admin', 'pre_approver', 'approver']}>
                <PromoImagesPage />
              </ProtectedRoute>
            }
          />

          {/* Flyer Routes (Supplier) */}
          <Route
            path="flyers"
            element={
              <ProtectedRoute allowedRoles={['supplier']}>
                <FlyersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="flyers/:id"
            element={
              <ProtectedRoute allowedRoles={['supplier']}>
                <FlyerEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Approver Routes */}
          <Route
            path="approvals"
            element={
              <ProtectedRoute allowedRoles={['approver', 'pre_approver']}>
                <ApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/:approvalId"
            element={
              <ProtectedRoute allowedRoles={['approver', 'pre_approver']}>
                <ApprovalReviewPage />
              </ProtectedRoute>
            }
          />

          {/* Active Flyers Routes (Admin + Approver + Pre-Approver + End User) */}
          <Route
            path="active-flyers"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver', 'pre_approver', 'end_user']}>
                <ActiveFlyersPage />
              </ProtectedRoute>
            }
          />

          {/* Náhled letáku jen ke čtení. Editor /flyers/:id je vyhrazený autorovi,
              tohle je pohled pro admina a schvalovatelskou dvojici. */}
          <Route
            path="active-flyers/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'approver', 'pre_approver']}>
                <FlyerPreviewPage />
              </ProtectedRoute>
            }
          />

          {/* My Flyers Routes (End User) - uses same pages as supplier */}
          <Route
            path="my-flyers"
            element={
              <ProtectedRoute allowedRoles={['end_user']}>
                <FlyersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-flyers/new"
            element={
              <ProtectedRoute allowedRoles={['end_user']}>
                <FlyerEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-flyers/:id"
            element={
              <ProtectedRoute allowedRoles={['end_user']}>
                <FlyerEditorPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
